import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Linkedin, Sparkles, Copy, Check, ShieldCheck, ArrowLeft, Zap, PenLine } from "lucide-react";
import client from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

// Warm human typing cadence, mirrored from the read so Klair's rewrites
// arrive the same way her line does: a small base delay per character, a
// tiny beat at sentence ends, a touch of jitter, and a catch-up while a lot
// is still buffered so she never lags far behind her own thought.
function typingDelay(ch, buffered) {
  let d = 9 + Math.random() * 15;
  if (ch === "." || ch === "!" || ch === "?") d += 150 + Math.random() * 130;
  else if (ch === "," || ch === ";" || ch === ":") d += 70 + Math.random() * 60;
  else if (ch === "\n") d += 160;
  else if (ch === " ") d += 5;
  if (buffered > 160) d *= 0.3;
  else if (buffered > 80) d *= 0.55;
  return d;
}
const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const tid = (key) => key.replace(/[^a-z0-9]+/gi, "-");

/**
 * LinkedIn optimiser. Klair rewrites the person's own material, section by
 * section, streaming her words in live the same warm way the read does.
 * Every rewrite is checked against the person's own facts by the
 * no-fabrication gate on the server; a rewrite that drifts is discarded and
 * the person's own line is kept, shown with a quiet "kept your words" note.
 */
export default function LinkedinScreen({ product }) {
  const nav = useNavigate();
  const base = product === "grow" ? "/grow" : "";
  const [resume, setResume] = useState({ headline: "", summary: "" });
  const [experiences, setExperiences] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [sections, setSections] = useState([]);
  const [display, setDisplay] = useState({});
  const [snappy, setSnappy] = useState(false);
  const [err, setErr] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);
  const [targetRole, setTargetRole] = useState("");

  const secRef = useRef({});
  const orderRef = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const [r, e] = await Promise.all([
          client.get("/resume"),
          client.get("/resume/experience"),
        ]);
        setResume({ headline: r.data?.headline || "", summary: r.data?.summary || "" });
        setExperiences(e.data?.items || []);
      } catch (_e) { /* first time */ }
      setLoaded(true);
    })();
  }, []);

  const hasData = !!(
    resume.headline || resume.summary ||
    experiences.some((e) => (e.bullets || []).length)
  );

  // Reveal each section's buffered text at a warm pace, then settle to the
  // authoritative (gate-checked) text once the section stream is done.
  const consume = async () => {
    const reduced = reducedMotion();
    for (const key of orderRef.current) {
      while (!secRef.current[key]) await sleep(30);
      setDisplay((d) => ({ ...d, [key]: { text: "", streaming: true } }));
      let revealed = 0;
      for (;;) {
        const s = secRef.current[key];
        const buf = s.buf;
        if (revealed < buf.length) {
          revealed += 1;
          const ch = buf[revealed - 1];
          setDisplay((d) => ({ ...d, [key]: { text: buf.slice(0, revealed), streaming: true } }));
          const remaining = s.streamDone ? 0 : buf.length - revealed;
          await sleep(reduced ? 0 : typingDelay(ch, remaining));
        } else if (s.streamDone) {
          break;
        } else {
          await sleep(40);
        }
      }
      const s = secRef.current[key];
      const finalText = (s.final != null ? s.final : s.buf) || "";
      setDisplay((d) => ({
        ...d,
        [key]: { text: finalText, streaming: false, done: true, usedRewrite: !!s.usedRewrite },
      }));
    }
  };

  const run = async () => {
    if (running) return;
    secRef.current = {};
    orderRef.current = [];
    setSections([]);
    setDisplay({});
    setSnappy(false);
    setErr("");
    setRunning(true);
    const token = localStorage.getItem("klareet.access");
    let consumerP = null;
    try {
      const res = await fetch(`${API}/api/resume/linkedin-optimise/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ goal: "", quotes: [], target_role: targetRole.trim() }),
      });
      if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let sep;
        while ((sep = buf.indexOf("\n\n")) >= 0) {
          const frame = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          const line = frame.replace(/^data:\s?/, "").trim();
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line); } catch (_e) { continue; }
          if (ev.type === "start") {
            orderRef.current = (ev.sections || []).map((s) => s.key);
            for (const s of ev.sections || []) {
              secRef.current[s.key] = { label: s.label, buf: "", streamDone: false, final: null, usedRewrite: false };
            }
            setSections(ev.sections || []);
            consumerP = consume();
          } else if (ev.type === "section_start") {
            if (!secRef.current[ev.key]) {
              secRef.current[ev.key] = { label: ev.label, buf: "", streamDone: false, final: null, usedRewrite: false };
            }
          } else if (ev.type === "token") {
            const s = secRef.current[ev.key];
            if (s) s.buf += ev.text || "";
          } else if (ev.type === "section_done") {
            const s = secRef.current[ev.key] ||
              (secRef.current[ev.key] = { label: ev.label, buf: "", streamDone: false, final: null, usedRewrite: false });
            s.final = ev.text || "";
            s.usedRewrite = !!ev.used_rewrite;
            s.streamDone = true;
            if (ev.fast) setSnappy(true);
          } else if (ev.type === "done") {
            for (const k of orderRef.current) {
              if (secRef.current[k]) secRef.current[k].streamDone = true;
            }
          } else if (ev.type === "error") {
            setErr("Klair could not finish that. Please try again.");
          }
        }
      }
      if (consumerP) await consumerP;
    } catch (_e) {
      setErr("Could not reach Klair. Please try again.");
    } finally {
      setRunning(false);
    }
  };

  const copySection = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1400);
    } catch (_e) { /* ignore */ }
  };

  const copyAll = async () => {
    const parts = sections
      .map((s) => {
        const d = display[s.key];
        return d && d.text ? `${s.label}\n${d.text}` : null;
      })
      .filter(Boolean);
    if (!parts.length) return;
    try {
      await navigator.clipboard.writeText(parts.join("\n\n"));
      setCopiedKey("__all__");
      setTimeout(() => setCopiedKey(null), 1400);
    } catch (_e) { /* ignore */ }
  };

  const allSettled = sections.length > 0 && sections.every((s) => display[s.key]?.done);

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24">
      <div className="k-fade-in mb-6">
        <button
          onClick={() => nav(`${base}/resume`)}
          className="inline-flex items-center gap-1 text-sm k-quiet hover:opacity-80 mb-3"
          data-testid="linkedin-back"
        >
          <ArrowLeft size={14} /> back to resume
        </button>
        <div className="k-label mb-2 flex items-center gap-2">
          <Linkedin size={13} /> linkedin optimiser
        </div>
        <h1
          className="klareet-serif mb-2"
          style={{ fontSize: "clamp(1.8rem, 4vw, 2.4rem)", lineHeight: 1.1 }}
          data-testid="linkedin-title"
        >
          Klair polishes your profile.
        </h1>
        <p className="k-hero-sub">
          From your own words only. If a rewrite ever drifts from what you actually said, Klair keeps your original. Nothing invented.
        </p>
      </div>

      {loaded && !hasData && (
        <div className="k-warm-card p-6" data-testid="linkedin-empty">
          <div className="font-semibold mb-1" style={{ color: "hsl(var(--klareet-ink))" }}>
            Add a little first.
          </div>
          <p className="text-sm mb-3" style={{ color: "hsl(var(--klareet-ink-soft))" }}>
            Klair rewrites from what you have written. Add a headline, a short summary, or an experience, then come back.
          </p>
          <button onClick={() => nav(`${base}/resume`)} className="k-btn k-btn-primary" data-testid="linkedin-go-resume">
            <PenLine size={14} /> Open your resume
          </button>
        </div>
      )}

      {loaded && hasData && (
        <>
          <div className="k-card mb-6" data-testid="linkedin-run-card">
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>
              Ready when you are
            </div>
            <p className="k-quiet !text-sm mb-3">
              Klair will rewrite your headline, your about, and your experience, one section at a time.
            </p>
            <label className="k-label mb-1 block" htmlFor="linkedin-target-role">aiming for a role? (optional)</label>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <input
                id="linkedin-target-role"
                className="k-input flex-1"
                placeholder="e.g. junior product manager, data analyst"
                value={targetRole}
                maxLength={120}
                onChange={(e) => setTargetRole(e.target.value)}
                data-testid="linkedin-target-role"
              />
              <div className="flex items-center gap-2 shrink-0">
                {snappy && (
                  <span
                    className="k-snappy-cue inline-flex items-center"
                    title="Klair answered extra quickly just now"
                    data-testid="snappy-hint"
                    aria-label="Klair answered quickly"
                  >
                    <Zap size={11} strokeWidth={2.25} />
                  </span>
                )}
                <button onClick={run} disabled={running} className="k-btn k-btn-accent" data-testid="linkedin-run-btn">
                  <Sparkles size={14} /> {running ? "Klair is writing" : (sections.length ? "Polish again" : "Let Klair polish this")}
                </button>
              </div>
            </div>
            <p className="k-quiet !text-xs mt-2">
              A role only angles the tone. Klair still writes only from what you have actually done.
            </p>
          </div>

          {err && (
            <div className="mb-4 text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="linkedin-error">
              {err}
            </div>
          )}

          {sections.length > 0 && (
            <div className="space-y-4" data-testid="linkedin-sections">
              {sections.map((s) => {
                const d = display[s.key] || { text: "", streaming: false };
                const settled = d.done;
                return (
                  <div key={s.key} className="k-card k-fade-in" data-testid={`linkedin-section-${tid(s.key)}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="k-label">{s.label}</div>
                      {settled && d.text && (
                        settled && d.usedRewrite ? (
                          <span className="k-chip k-chip-teal !text-[10px]" data-testid={`linkedin-badge-${tid(s.key)}`}>
                            <ShieldCheck size={10} /> polished by Klair
                          </span>
                        ) : (
                          <span className="k-chip !text-[10px]" data-testid={`linkedin-badge-${tid(s.key)}`}>
                            kept your words
                          </span>
                        )
                      )}
                    </div>
                    {d.text ? (
                      <p
                        className="klareet-serif"
                        style={{ whiteSpace: "pre-line", color: "hsl(var(--klareet-ink))", lineHeight: 1.55 }}
                        data-testid={`linkedin-text-${tid(s.key)}`}
                      >
                        {d.text}
                        {d.streaming && <span className="k-caret" aria-hidden="true" />}
                      </p>
                    ) : (
                      <span className="k-think-dot" data-testid={`linkedin-thinking-${tid(s.key)}`} />
                    )}
                    {settled && d.text && (
                      <div className="mt-3">
                        <button
                          onClick={() => copySection(s.key, d.text)}
                          className="k-btn-outline !py-1.5 !px-3 text-xs"
                          data-testid={`linkedin-copy-${tid(s.key)}`}
                        >
                          {copiedKey === s.key ? <><Check size={12} /> copied</> : <><Copy size={12} /> copy</>}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {allSettled && (
                <div className="flex justify-end">
                  <button onClick={copyAll} className="k-btn k-btn-primary" data-testid="linkedin-copy-all">
                    {copiedKey === "__all__" ? <><Check size={14} /> copied everything</> : <><Copy size={14} /> copy everything</>}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
